import { projectCustomerNavigation } from "../../lib/simpleNavigation";
import { canWriteClient } from "../../lib/clientPermissions";
import React, { useEffect, useState, useRef } from "react";
import { NavLink, Outlet, useNavigate, useParams, useLocation } from "react-router-dom";
import { LayoutGrid, MessageCircle, BarChart3, Sparkles, FileText, FolderOpen, LogOut, ArrowLeft, Menu, X, Users, ShieldCheck, Send, Globe, Star, LayoutTemplate, Lightbulb, LockKeyhole, ReceiptText, Upload, Heart, CheckCircle2, RefreshCcw, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LANGS } from "@/context/I18nContext";
import { Spinner, Avatar } from "@/components/common";
import EnvBadge from "@/components/EnvBadge";
import { websiteReachabilityVerified } from "@/lib/environmentEvidence";
import NotificationBell from "@/components/NotificationBell";
import { CockpitTopBar } from "./TopBar";
import AiAssistDrawer from "./AiAssistDrawer";
import OnboardingTour from "./OnboardingTour";
import CockpitHelp from "./CockpitHelp";
import { buildChecklist } from "./checklist";
import { CockpitCtx, useCockpit } from "./cockpitContext";
import useCockpitOverview from "./useCockpitOverview";

export { useCockpit };

export default function CockpitLayout() {
  const { logout, user, setUser } = useAuth();
  const { lang, setLang, setDefaultLang, t } = useI18n();
  const { clientId: paramClientId } = useParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showMoreAreas, setShowMoreAreas] = useState(true);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("kewag_sidebar_collapsed") === "1");
  const [tip, setTip] = useState(null); // {label, badge, top} — Tooltip im eingeklappten Zustand
  const toggleCollapsed = () => setCollapsed((c) => { const n = !c; localStorage.setItem("kewag_sidebar_collapsed", n ? "1" : "0"); return n; });
  const [aiOpen, setAiOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourPhase, setTourPhase] = useState("welcome");
  const [tourHighlight, setTourHighlight] = useState(null);
  const autoShownRef = useRef(false);
  const nav = useNavigate();
  const loc = useLocation();

  const isStaff = ["super_admin", "team_member"].includes(user.role);
  const clientId = isStaff ? paramClientId : null;
  const canEdit = isStaff && !!clientId && canWriteClient(user, clientId);

  const { data, loadError, load } = useCockpitOverview({ lang, clientId,
    userId: user.id, accountClientId: user.client_id });
  const loadRecommendation = React.useCallback(
    (skipList = []) => api.get(`/cockpit/ai/first-recommendation?lang=${lang}${clientId ? `&client_id=${clientId}` : ""}${skipList.length ? `&skip=${skipList.join(",")}` : ""}`).then((r) => r.data),
    [lang, clientId]
  );

  // PWA-Nutzung melden (nur echte Kundennutzer, keine Impersonation) — misst App-Adaption
  useEffect(() => {
    if (clientId || !["client", "client_staff"].includes(user?.role)) return;
    const ua = navigator.userAgent || "";
    const platform = /iPhone|iPad|iPod/i.test(ua) ? "ios" : /Android/i.test(ua) ? "android" : "desktop";
    const isStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator?.standalone === true;
    const report = (event) => {
      const last = Number(localStorage.getItem("kewag_pwa_reported") || 0);
      if (event !== "appinstalled" && Date.now() - last < 12 * 3600 * 1000) return;
      localStorage.setItem("kewag_pwa_reported", String(Date.now()));
      api.post("/cockpit/pwa/installed", { platform, standalone: !!isStandalone, event, ua }).catch(() => {});
    };
    if (isStandalone) report("launch");
    const onInstalled = () => report("appinstalled");
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, [clientId, user]);
  useEffect(() => { if (data?.client?.language) setDefaultLang(data.client.language); }, [data]);
  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);
  useEffect(() => {
    if (user.role !== "client_staff") return;
    const seg = loc.pathname.split("/").filter(Boolean).pop();
    const section = !seg || seg === "cockpit" ? "overview" : seg === "reports" ? "report" : seg;
    const allowed = user.allowed_sections || [];
    const ok = section === "overview" || section === "security"
      || allowed.includes(section)
      || (section === "requests" && (allowed.includes("support") || allowed.includes("requests")))
      ;
    if (!ok) {
      nav("/cockpit", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname]);
  // Onboarding-Rundgang beim ersten Login automatisch zeigen (nur echter Kunden-Login, nicht Impersonation)
  useEffect(() => {
    if (!data || autoShownRef.current) return;
    if (clientId) return;
    if (!["client", "client_staff"].includes(user.role)) return;
    const done = user.onboarding_seen || user.onboarding_dismissed_at || localStorage.getItem("kewag_onboarding_seen");
    if (!done) { autoShownRef.current = true; setTourPhase("welcome"); setTourOpen(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, clientId]);
  useEffect(() => {
    if (tourHighlight) document.querySelector(`[data-testid="side-${tourHighlight}"]`)?.scrollIntoView({ block: "nearest" });
  }, [tourHighlight]);
  if (!data) return loadError ? (
    <div data-testid="cockpit-load-error" className="min-h-screen bg-[#0A0A0C] text-white flex items-center justify-center p-6">
      <div className="text-center max-w-sm">
        <p className="text-gray-300 text-sm mb-4">{t("Ihre Übersicht konnte nicht geladen werden. Bitte versuchen Sie es erneut.")}</p>
        <button data-testid="cockpit-load-retry" onClick={load}
          className="inline-flex items-center gap-2 bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold px-5 py-2.5 rounded-lg transition-colors">
          <RefreshCcw className="w-4 h-4" /> {t("Erneut versuchen")}
        </button>
      </div>
    </div>
  ) : <Spinner />;

  const client = data.client;
  const accent = client.primary_color || "#A3E635";
  const base = clientId ? `/admin/cockpit/${clientId}` : "/cockpit";

  // EINZIGE flache Navigation — Reihenfolge exakt nach Kundenreferenz. Keine Gruppen.
  const baseNav = [
    { to: base, icon: LayoutGrid, label: t("Überblick"), end: true, key: "overview" },
    { to: `${base}/website`, icon: Globe, label: t("Website"), key: "website" },
    { to: `${base}/documents`, icon: Upload, label: t("Dateien & Freigaben"), key: "documents" },
    { to: `${base}/tasks`, icon: CheckCircle2, label: t("Aufgaben & Freigaben"), key: "tasks" },
    { to: `${base}/freigaben`, icon: CheckCircle2, label: t("Entwürfe freigeben"), key: "freigaben" },
    { to: `${base}/requests`, icon: Heart, label: t("Meine Wünsche"), key: "requests" },
    { to: `${base}/support`, icon: MessageCircle, label: t("Support"), key: "support" },
    { to: `${base}/design`, icon: Lightbulb, label: t("Website verbessern"), key: "design", pill: t("NEU") },
    { to: `${base}/reviews`, icon: Star, label: t("Bewertungen"), key: "reviews" },
    { to: `${base}/reports`, icon: FileText, label: t("Monatsreport"), key: "report" },
    { to: `${base}/performance`, icon: BarChart3, label: t("Besuche & Performance"), key: "performance" },
    { to: `${base}/invoices`, icon: ReceiptText, label: t("Rechnungen"), key: "invoices" },
    { to: `${base}/next-steps`, icon: Sparkles, label: t("Nächste Schritte"), key: "next-steps" },
    { to: `${base}/team`, icon: Users, label: t("Team & Benutzer"), key: "team" },
    { to: `${base}/security`, icon: ShieldCheck, label: t("Konto & Sicherheit"), key: "security" },
  ];
  // Design-Bibliothek (intern) — nur fuer KEWAG-Team, verlinkt in den Admin-Bereich
  if (isStaff) baseNav.push({ to: "/admin/design-library", icon: LockKeyhole, label: t("Design-Bibliothek (intern)"), key: "design-library" });

  const allowFilter = (it) => {
    // Team & Benutzer nur fuer Kunden-Admin oder Super-Admin-Ansicht (Impersonation)
    if (it.key === "team") return user.role === "client" || !!clientId;
    if (user.role !== "client_staff") return true;
    if (it.key === "overview" || it.key === "security") return true;
    if (it.key === "documents") return (user.allowed_sections || []).includes("documents");
    if (it.key === "invoices") return (user.allowed_sections || []).includes("invoices");
    if (it.key === "requests") return (user.allowed_sections || []).some((s) => s === "support" || s === "requests");
    return (user.allowed_sections || []).includes(it.key);
  };
  const navFlat = baseNav.filter(allowFilter);
  const allItems = navFlat;
  const simpleCustomerNav = projectCustomerNavigation(navFlat, loc.pathname, showMoreAreas);
  // Mobile-Bottom-Bar: kompakte Schnellzugriffe aus der flachen Navigation
  const BOTTOM_KEYS = ["overview", "website", "documents", "requests", "support"];
  const tabs = BOTTOM_KEYS.map((k) => navFlat.find((i) => i.key === k)).filter(Boolean);

  const navItemStyle = ({ isActive }) => isActive ? { background: `${accent}1A`, color: accent } : {};
  const counts = { leads: data.open_leads, tasks: data.open_tasks, support: data.open_support, invoices: data.open_invoices, freigaben: data.open_freigaben, requests: data.open_requests };
  const badgeFor = (key) => (counts[key] > 0 ? counts[key] : null);
  const contact = (data.care_team && data.care_team[0]) || null;
  const sysOnline = websiteReachabilityVerified(data.website);
  const goSupport = () => nav(`${base}/support`);

  const patchOnboarding = (action) => api.patch("/auth/me/onboarding", { action, language: lang }).then(({ data: r }) => { if (r.user) setUser(r.user); }).catch(() => {});
  const closeTour = () => { setTourOpen(false); setTourHighlight(null); };
  const startTour = () => { setTourPhase("tour"); setTourOpen(true); };
  const completeSteps = () => { localStorage.setItem("kewag_onboarding_seen", "1"); patchOnboarding("completed"); };
  const goFromTour = (to) => { closeTour(); nav(`${base}/${to}`); };
  const dismissTour = (never) => { localStorage.setItem("kewag_onboarding_seen", "1"); closeTour(); patchOnboarding(never ? "never" : "dismissed"); };
  const openAiFromTour = () => { closeTour(); setAiOpen(true); patchOnboarding("ai_from_tour"); };
  const checklistItems = buildChecklist({ user, overview: data }).map((it) => (it.key === "tour" ? { ...it, done: true } : it));
  const navStyle = (state, key) => ({
    ...(state.isActive ? { background: `${accent}1A`, color: accent } : {}),
    ...(tourHighlight === key ? { boxShadow: `0 0 0 2px ${accent}, 0 0 22px -2px ${accent}`, background: `${accent}14`, color: "#fff" } : {}),
  });
  const FOOTER_LINKS = [
    { label: t("Datenschutz"), href: "https://kewagpage.ch/datenschutz" },
    { label: t("AGB"), href: "https://kewagpage.ch/agb" },
    { label: t("Impressum"), href: "https://kewagpage.ch/impressum" },
  ];

  const showTip = (e, it) => {
    if (!collapsed) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ label: it.label, badge: badgeFor(it.key), top: r.top + r.height / 2 });
  };
  const hideTip = () => setTip(null);

  const renderNav = (it) => (
    <NavLink key={it.to} to={it.to} end={it.end} data-testid={`side-${it.key}`}
      onMouseEnter={(e) => showTip(e, it)} onMouseLeave={hideTip} onClick={hideTip}
      className={`group relative flex items-center ${collapsed ? "justify-center px-0" : "gap-3 px-3"} py-2.5 rounded-xl text-[13px] font-medium text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors`}
      style={(s) => navStyle(s, it.key)}>
      {({ isActive }) => (<>
        {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full" style={{ background: accent, boxShadow: `0 0 12px ${accent}80` }} />}
        <span className="relative shrink-0 flex items-center justify-center">
          <it.icon className="w-[18px] h-[18px]" strokeWidth={2} />
          {collapsed && (badgeFor(it.key) || it.pill) && <span className="absolute -top-1.5 -right-1.5 w-2 h-2 rounded-full ring-2 ring-[#0D0E12]" style={{ background: accent }} />}
        </span>
        {!collapsed && <span className="flex-1 truncate text-left">{it.label}</span>}
        {!collapsed && it.pill && !badgeFor(it.key) && (
          <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full shrink-0" data-testid={`side-pill-${it.key}`}
            style={{ background: accent, color: "#000" }}>{it.pill}</span>
        )}
        {!collapsed && badgeFor(it.key) && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 border" data-testid={`side-badge-${it.key}`}
            style={isActive ? { background: accent, color: "#000", borderColor: accent } : { background: "rgba(255,255,255,0.05)", color: "#d1d5db", borderColor: "rgba(255,255,255,0.1)" }}>{badgeFor(it.key)}</span>
        )}
      </>)}
    </NavLink>
  );

  return (
    <CockpitCtx.Provider value={{ overview: data, navigation: navFlat, accent, clientId, base, canEdit, refresh: load, openAi: () => setAiOpen(true), startTour, tourHighlight }}>
      <div className="min-h-screen bg-[#0A0A0C] text-white" style={{ "--client-accent": accent }}>
        {/* ===== Desktop sidebar ===== */}
        <aside className={`hidden lg:flex fixed top-0 left-0 z-40 h-screen flex-col bg-[#0D0E12] border-r border-white/[0.06] transition-[width] duration-300 ${collapsed ? "w-[76px]" : "w-[260px]"}`}
          style={{ transitionTimingFunction: "cubic-bezier(0.16,1,0.3,1)" }}>
          {/* Brand + collapse */}
          <div className={`flex items-center h-16 border-b border-white/[0.06] ${collapsed ? "justify-center px-2" : "justify-between px-5"}`}>
            {!collapsed && (
              <div>
                <div className="text-[15px] font-black tracking-tight leading-none">KEWAG<span style={{ color: accent }}>page</span></div>
                <div className="text-[8px] text-gray-500 uppercase tracking-[0.3em] mt-1">Control OS</div>
              </div>
            )}
            <button onClick={toggleCollapsed} data-testid="side-collapse-toggle" title={collapsed ? t("Menü ausklappen") : t("Menü einklappen")}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-colors">
              {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
            </button>
          </div>

          {/* Client identity pill */}
          <div className={collapsed ? "px-2 pt-3 pb-1" : "px-3 pt-3 pb-1"}>
            <div className={`flex items-center rounded-xl bg-white/[0.03] border border-white/[0.06] transition-colors hover:bg-white/[0.05] ${collapsed ? "justify-center p-2" : "gap-3 p-2.5"}`} data-testid="side-client-pill">
              <Avatar name={client.company_name} color={accent} src={client.logo_url || null} size={collapsed ? 34 : 36} />
              {!collapsed && (
                <div className="min-w-0">
                  <div className="font-semibold text-[13px] truncate">{client.company_name}</div>
                  <div className="text-[9px] text-gray-500 uppercase tracking-[0.2em] mt-0.5">{t("Kunden-Cockpit")}</div>
                </div>
              )}
            </div>
          </div>

          {/* Flache Navigation — eine Liste, keine Gruppen */}
          <nav className="flex-1 overflow-y-auto sidebar-scroll px-2 py-3 space-y-1" data-testid="cockpit-sidebar-nav">
            {simpleCustomerNav.items.map(renderNav)}
            {simpleCustomerNav.extraCount > 0 && <button type="button" data-testid="cockpit-nav-more" aria-expanded={showMoreAreas} title={t(showMoreAreas ? "Weniger Bereiche anzeigen" : "Weitere Bereiche")} onClick={() => setShowMoreAreas(v => !v)} className="w-full min-h-[44px] flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-300 hover:bg-white/5">
              <Menu className="w-5 h-5 shrink-0" aria-hidden="true"/><span className={collapsed ? "sr-only" : "text-left"}>{t(showMoreAreas ? "Weniger Bereiche anzeigen" : "Weitere Bereiche")}</span>
            </button>}
          </nav>

          {/* Ansprechpartner */}
          {!collapsed && (
            <div className="px-3 pt-2">
              <div className="rounded-xl bg-gradient-to-b from-white/[0.05] to-transparent border border-white/[0.06] p-3.5 backdrop-blur-sm transition-all" data-testid="side-contact-card"
                style={tourHighlight === "contact" ? { boxShadow: `0 0 0 2px ${accent}, 0 0 22px -2px ${accent}` } : {}}>
                <div className="text-[9px] uppercase tracking-[0.2em] text-white/40 mb-2.5 font-bold">{t("Ihr Ansprechpartner")}</div>
                <div className="flex items-center gap-3">
                  {contact?.picture
                    ? <img src={contact.picture} alt={contact.name} className="w-9 h-9 rounded-full object-cover shrink-0" />
                    : <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: `${accent}22`, color: accent }}>{(contact?.name || "KEWAG").split(" ").map((p) => p[0]).slice(0, 2).join("")}</div>}
                  <div className="min-w-0">
                    <div className="font-semibold text-[13px] truncate">{contact?.name || t("KEWAG Support-Team")}</div>
                    <div className="text-[11px] text-gray-500 truncate">{contact ? t(contact.function) : t("Ihr Digitaler Partner")}</div>
                  </div>
                </div>
                <button onClick={() => setAiOpen(true)} data-testid="side-contact-message"
                  className="w-full mt-3 flex items-center justify-center gap-2 text-xs font-semibold py-2 rounded-lg border border-white/10 bg-white/10 hover:bg-white/15 transition-colors text-white">
                  <Send className="w-3.5 h-3.5" /> {t("Nachricht senden")}
                </button>
              </div>
            </div>
          )}

          {/* Systemstatus + Sprache + Logout */}
          <div className="border-t border-white/[0.06] mt-2">
            {collapsed ? (
              <div className="flex flex-col items-center gap-3 py-3" data-testid="side-system-status">
                <span className="relative flex h-2.5 w-2.5" title={sysOnline ? t("Website erreichbar") : t("Wird betreut")}>
                  <span className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping" style={{ background: sysOnline ? "#22C55E" : "#F59E0B" }} />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: sysOnline ? "#22C55E" : "#F59E0B" }} />
                </span>
                <button onClick={() => clientId ? nav(`/admin/clients/${clientId}`) : logout()} data-testid="side-logout"
                  className="text-gray-500 hover:text-white transition-colors" title={clientId ? t("Zurück zum Admin") : t("Abmelden")}>
                  {clientId ? <ArrowLeft className="w-5 h-5" /> : <LogOut className="w-5 h-5" />}
                </button>
              </div>
            ) : (
              <>
                <div className="px-3 pt-3">
                  <div className="flex items-center gap-2.5 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5" data-testid="side-system-status">
                    <span className="relative flex h-2.5 w-2.5 shrink-0">
                      <span className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping" style={{ background: sysOnline ? "#22C55E" : "#F59E0B" }} />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: sysOnline ? "#22C55E" : "#F59E0B" }} />
                    </span>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold truncate">{sysOnline ? t("Website erreichbar") : t("Wird betreut")}</div>
                      <div className="text-[10px] text-gray-500 truncate">KEWAGpage · {t("Ihr Digitaler Partner")}</div>
                    </div>
                  </div>
                </div>
                <div className="p-3 space-y-3">
                  <div className="flex gap-1">
                    {LANGS.map((l) => (
                      <button key={l.code} onClick={() => setLang(l.code)} data-testid={`side-lang-${l.code}`}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-colors ${lang === l.code ? "text-black" : "text-gray-500 hover:text-white"}`}
                        style={lang === l.code ? { background: accent } : {}}>{l.label}</button>
                    ))}
                  </div>
                  <button onClick={() => clientId ? nav(`/admin/clients/${clientId}`) : logout()} data-testid="side-logout"
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-white w-full transition-colors">
                    {clientId ? <><ArrowLeft className="w-4 h-4" /> {t("Zurück zum Admin")}</> : <><LogOut className="w-4 h-4" /> {t("Abmelden")}</>}
                  </button>
                  <EnvBadge />
                </div>
              </>
            )}
          </div>
        </aside>

        {/* ===== Sidebar-Tooltip (nur eingeklappt, Desktop) ===== */}
        {collapsed && tip && (
          <div className="hidden lg:block fixed z-[60] pointer-events-none side-tip-in" data-testid="side-tooltip"
            style={{ left: 84, top: tip.top, transform: "translateY(-50%)" }}>
            <div className="relative flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#16171d] border border-white/10 shadow-xl shadow-black/50 whitespace-nowrap">
              <span className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 rotate-45 bg-[#16171d] border-l border-b border-white/10" />
              <span className="text-[12.5px] font-semibold text-white" data-testid="side-tooltip-label">{tip.label}</span>
              {tip.badge && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: accent, color: "#000" }}>{tip.badge}</span>}
            </div>
          </div>
        )}

        {/* ===== Mobile app bar ===== */}
        <header className="lg:hidden sticky top-0 z-40 backdrop-blur-xl bg-[#0A0A0C]/85 border-b border-white/10">
          <div className="px-4 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Avatar name={client.company_name} color={accent} src={client.logo_url || null} size={34} />
              <div className="leading-none min-w-0">
                <div className="font-extrabold tracking-tight text-[15px] truncate" title={client.company_name} data-testid="mobile-company-name">{client.company_name}</div>
                {clientId ? (
                  <span className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded mt-1" data-testid="mobile-impersonation-badge" style={{ background: "rgba(245,158,11,0.16)", color: "#fbbf24" }}>
                    <ShieldCheck className="w-2.5 h-2.5" /> {t("Super-Admin-Ansicht")}
                  </span>
                ) : (
                  <div className="text-[9px] text-gray-500 uppercase tracking-[0.22em] mt-0.5">{t("Kunden-Cockpit")}</div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {clientId && (
                <button onClick={() => nav(`/admin/clients/${clientId}`)} data-testid="mobile-back-admin" className="p-2 text-gray-300 hover:text-white" title={t("Zurück zum Admin")}>
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <NotificationBell clientId={clientId} accent={accent} base={base} />
              <CockpitHelp variant="mobile" />
              <button onClick={() => setMenuOpen((o) => !o)} data-testid="cockpit-menu-toggle" aria-label={t("Menü")} aria-expanded={menuOpen} className="p-2 -mr-2 text-gray-300 hover:text-white">
                {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </header>

        {menuOpen && (
          <>
            <div className="lg:hidden fixed inset-0 z-30 bg-black/40" onClick={() => setMenuOpen(false)} />
            <div className="lg:hidden fixed top-16 inset-x-0 z-40 bg-[#101015] border-b border-white/10 shadow-2xl" data-testid="cockpit-menu">
              <div className="px-4 py-4">
                <div className="grid grid-cols-1 gap-0.5" data-testid="cockpit-menu-list">
                  {simpleCustomerNav.items.map((it) => (
                    <NavLink key={it.to} to={it.to} end={it.end} data-testid={`menu-${it.key}`}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition-colors text-gray-300" style={navItemStyle}>
                      <it.icon className="w-5 h-5 shrink-0" /> <span className="font-medium text-sm flex-1 truncate">{it.label}</span>
                      {it.pill && !badgeFor(it.key) && (
                        <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full shrink-0" style={{ background: accent, color: "#000" }}>{it.pill}</span>
                      )}
                      {badgeFor(it.key) && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0" style={{ background: `${accent}22`, color: accent }}>{badgeFor(it.key)}</span>
                      )}
                    </NavLink>
                  ))}
                </div>
                {simpleCustomerNav.extraCount > 0 && <button type="button" aria-expanded={showMoreAreas} onClick={() => setShowMoreAreas(v => !v)} className="w-full min-h-[44px] text-left text-sm text-gray-300 px-3 py-2">{t(showMoreAreas ? "Weniger Bereiche anzeigen" : "Weitere Bereiche")}</button>}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                  <div className="flex gap-1">
                    {LANGS.map((l) => (
                      <button key={l.code} onClick={() => setLang(l.code)} data-testid={`cock-lang-${l.code}`}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-bold ${lang === l.code ? "text-black" : "text-gray-500 hover:text-white"}`}
                        style={lang === l.code ? { background: accent } : {}}>{l.label}</button>
                    ))}
                  </div>
                  <button onClick={() => clientId ? nav(`/admin/clients/${clientId}`) : logout()} data-testid="cockpit-logout"
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-white">
                    {clientId ? <><ArrowLeft className="w-4 h-4" /> {t("Admin")}</> : <><LogOut className="w-4 h-4" /> {t("Abmelden")}</>}
                  </button>
                </div>
                <div className="mt-4"><EnvBadge /></div>
              </div>
            </div>
          </>
        )}

        <main className={`${collapsed ? "lg:ml-[76px]" : "lg:ml-[260px]"} pb-28 lg:pb-12 transition-[margin] duration-300`}>
          <CockpitTopBar accent={accent} base={base} clientId={clientId} company={client.company_name} navItems={allItems} overview={data} user={user} t={t} status={sysOnline} onLogout={() => clientId ? nav(`/admin/clients/${clientId}`) : logout()} heading={null} />
          <div className="w-full px-4 py-5 lg:px-8 lg:py-8">
            <Outlet />
            <footer className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500" data-testid="cockpit-footer">
              <div>© {new Date().getFullYear()} KEWAGpage · {t("Alle Rechte vorbehalten")}</div>
              <div className="flex items-center gap-4 flex-wrap justify-center">
                {FOOTER_LINKS.map((l) => (
                  <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer" data-testid={`footer-${l.label.toLowerCase()}`} className="hover:text-gray-300 transition-colors">{l.label}</a>
                ))}
                <button onClick={() => setAiOpen(true)} data-testid="footer-help" className="hover:text-gray-300 transition-colors">{t("Hilfe & Support")}</button>
              </div>
            </footer>
          </div>
        </main>

        {/* ===== Mobile bottom tab bar ===== */}
        <nav data-testid="cockpit-bottom-nav" className="lg:hidden fixed bottom-0 inset-x-0 z-[10000] backdrop-blur-xl bg-[#0A0A0C]/95 border-t border-white/10" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          <div className="grid" style={{ gridTemplateColumns: `repeat(${Math.max(tabs.length, 1)}, minmax(0,1fr))` }}>
            {tabs.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} data-testid={`cnav-${it.key}`}
                className="relative flex flex-col items-center gap-1 pt-2.5 pb-2 text-gray-500 transition-colors active:scale-95"
                style={({ isActive }) => isActive ? { color: accent } : {}}>
                {({ isActive }) => (<>
                  {isActive && <span className="absolute top-0 h-[3px] w-9 rounded-full" style={{ background: accent }} />}
                  <span className="relative flex items-center justify-center w-11 h-7 rounded-full transition-colors" style={isActive ? { background: `${accent}1F` } : {}}>
                    <it.icon className="w-[21px] h-[21px]" strokeWidth={isActive ? 2.4 : 1.8} />
                    {badgeFor(it.key) && (
                      <span className="absolute -top-1 right-1 min-w-[15px] h-[15px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center text-black ring-2 ring-[#0A0A0C]" style={{ background: accent }}>{badgeFor(it.key)}</span>
                    )}
                  </span>
                  <span className="text-[10px] font-semibold leading-none">{it.label}</span>
                </>)}
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
      <AiAssistDrawer open={aiOpen} onClose={() => setAiOpen(false)} clientId={clientId} accent={accent} />
      <OnboardingTour open={tourOpen} initialPhase={tourPhase} accent={accent} navKeys={allItems.map((it) => it.key)} checklist={checklistItems} loadRecommendation={loadRecommendation}
        onClose={closeTour} onHighlight={setTourHighlight} onStart={() => patchOnboarding("started")}
        onComplete={completeSteps} onDismiss={dismissTour} onOpenAi={openAiFromTour} onGo={goFromTour} />
    </CockpitCtx.Provider>
  );
}


